import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Button,
    InputAdornment,
    Stack,
    FormControl,
    Select,
    MenuItem,
    CircularProgress,
    Alert
} from '@mui/material';
import {
    Search,
    RotateCcw,
    RefreshCw
} from 'lucide-react';
import HostKyc from './HostKyc';
import HostProfile from './HostProfile';
import { getAdminHostDetails } from '../../Services/Hosts/HostApi';
import GlobalPagination from '../../Components/Common/Globalpagination';

// Dropdown menu styling
const selectMenuProps = {
    PaperProps: {
        sx: {
            boxShadow: 'none',
            border: '1px solid #E2E8F0',
            borderRadius: '6px',
            mt: 0.5,
            p: 0,
            overflow: 'hidden',
            '& .MuiList-root': {
                py: 0.5,
            },
            '& .MuiMenuItem-root': {
                fontSize: '0.82rem',
                fontWeight: 500,
                py: 0.9,
                px: 1.5,
                color: '#1E293B',
                '&.Mui-selected': {
                    bgcolor: '#FFFDF9',
                    color: '#D97706',
                    fontWeight: 600,
                    '&:hover': {
                        bgcolor: '#FFF8EC'
                    }
                },
                '&:hover': {
                    bgcolor: '#F8FAFC'
                }
            }
        }
    }
};

// Filter labels map
const STATUS_LABELS = {
    active: 'Active',
    inactive: 'Inactive',
    pending: 'Pending',
    suspended: 'Suspended'
};

const KYC_LABELS = {
    verified: 'Verified',
    pending: 'Pending',
    rejected: 'Rejected'
};

// Badge styling configurations
const KYC_STYLES = {
    verified: { bg: '#E8F5E8', color: '#2E7D32', border: '#C8E6C9' },
    pending: { bg: '#FEF3C7', color: '#D97706', border: '#FDE68A' },
    rejected: { bg: '#FEE2E2', color: '#DC2626', border: '#FECACA' }
};

const STATUS_STYLES = {
    active: { bg: '#E8F5E8', color: '#2E7D32', border: '#C8E6C9' },
    'slow replies': { bg: '#FEF3C7', color: '#B45309', border: '#FDE68A' },
    onboarding: { bg: '#FFFBEB', color: '#B45309', border: '#FDE68A' },
    suspended: { bg: '#FEE2E2', color: '#DC2626', border: '#FECACA' }
};

const DEFAULT_BADGE_STYLE = { bg: '#F1F5F9', color: '#64748B', border: '#E2E8F0' };

// Format currency / GMV helper
const formatGmv = (amount) => {
    if (typeof amount === 'string' && (amount.startsWith('₹') || amount.includes('L') || amount.includes('K'))) {
        return amount;
    }
    const num = parseFloat(amount);
    if (isNaN(num) || num === 0) return '₹0';
    if (num >= 100000) {
        return `₹${(num / 100000).toFixed(1)}L`;
    }
    if (num >= 1000) {
        return `₹${(num / 1000).toFixed(0)}K`;
    }
    return `₹${num.toLocaleString('en-IN')}`;
};

const Hosts = () => {
    // API Data state
    const [hostsList, setHostsList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Search and filter states
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [kycFilter, setKycFilter] = useState("");

    // Pagination state (API-driven)
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [pagination, setPagination] = useState({
        totalItems: 0,
        totalPages: 1,
        currentPage: 1,
        limit: 10,
        hasNextPage: false,
        hasPrevPage: false
    });
    const [summary, setSummary] = useState(null);

    // View state: 'list', 'kyc', or 'profile'
    const [currentView, setCurrentView] = useState('list');
    const [selectedHostForKyc, setSelectedHostForKyc] = useState(null);
    const [selectedHostForProfile, setSelectedHostForProfile] = useState(null);

    const prevSearchRef = useRef(debouncedSearch);

    // Debounce search query (500ms delay after typing stops)
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Fetch hosts from API
    const fetchHosts = useCallback(async (currentPage = 1, currentLimit = limit, search = '', status = '', kyc = '') => {
        setLoading(true);
        setError(null);
        try {
            const params = {
                page: currentPage
            };
            if (currentLimit !== 10) params.limit = currentLimit;
            if (search.trim()) params.search = search.trim();
            if (status) params.status = status;
            if (kyc) params.kyc = kyc;

            const response = await getAdminHostDetails(params);
            const dataList = response?.hosts || response?.data?.hosts || response?.data || (Array.isArray(response) ? response : []);
            const paginationData = response?.pagination || {
                totalItems: dataList.length,
                totalPages: Math.ceil(dataList.length / currentLimit) || 1,
                currentPage: currentPage,
                limit: currentLimit,
                hasNextPage: false,
                hasPrevPage: false
            };

            setHostsList(dataList);
            setPagination(paginationData);
            setSummary(response?.summary || null);
        } catch (err) {
            console.error("Error fetching host details:", err);
            setError(err.response?.data?.message || err.message || "Failed to load host details from server.");
            setHostsList([]);
        } finally {
            setLoading(false);
        }
    }, [limit]);

    // Fetch hosts on page or filter changes without duplicate requests
    useEffect(() => {
        if (prevSearchRef.current !== debouncedSearch) {
            prevSearchRef.current = debouncedSearch;
            if (page !== 1) {
                setPage(1);
                return;
            }
        }
        fetchHosts(page, limit, debouncedSearch, statusFilter, kycFilter);
    }, [page, limit, debouncedSearch, statusFilter, kycFilter, fetchHosts]);

    // Helper to extract clean host data from API item
    const getHostData = (item) => {
        if (!item) return {};
        const id = item.id || item._id;
        const name = item.name || item.fullName || (item.firstName ? `${item.firstName} ${item.lastName || ''}`.trim() : '—');
        const email = item.email || item.contactEmail || '—';
        const location = item.location || item.city || item.state || '—';
        const listings = Number(item.listings ?? item.listingsCount ?? 0);
        const bookings = Number(item.bookings ?? item.bookingsCount ?? 0);
        const gmv = formatGmv(item.gmv ?? item.totalGmv ?? 0);
        const slab = item.slab || (item.commissionSlab ? `${item.commissionSlab}%` : '—');
        const rating = item.rating ?? item.avgRating ?? null;
        const kyc = (item.kyc || item.kycStatus || 'pending').toLowerCase();
        const status = (item.status || item.accountStatus || 'active').toLowerCase();

        return {
            raw: item,
            id,
            name,
            email,
            location,
            listings,
            bookings,
            gmv,
            slab,
            rating,
            kyc,
            status
        };
    };

    const processedHosts = hostsList.map(getHostData);
    const totalCount = summary?.totalCount ?? pagination.totalItems ?? processedHosts.length;
    const activeCount = summary?.activeCount ?? processedHosts.filter(h => h.status === 'active').length;

    const handleClearFilters = () => {
        setSearchTerm("");
        setDebouncedSearch("");
        setStatusFilter("");
        setKycFilter("");
        setPage(1);
    };

    const handleOpenKycView = (host) => {
        setSelectedHostForKyc(host);
        setCurrentView('kyc');
    };

    const handleOpenProfile = (host) => {
        setSelectedHostForProfile(host);
        setCurrentView('profile');
    };

    if (currentView === 'profile') {
        return (
            <HostProfile
                host={selectedHostForProfile}
                onBack={() => {
                    setCurrentView('list');
                    setSelectedHostForProfile(null);
                }}
                onOpenKyc={(host) => {
                    setSelectedHostForKyc(host);
                    setCurrentView('kyc');
                }}
            />
        );
    }

    if (currentView === 'kyc') {
        return (
            <HostKyc
                selectedHost={selectedHostForKyc}
                onBack={() => {
                    setCurrentView('list');
                    setSelectedHostForKyc(null);
                }}
            />
        );
    }

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#FBFBFB', minHeight: '100vh' }}>
            {/* ── Top Header Bar ── */}
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                spacing={1.5}
                sx={{ mb: 3 }}
            >
                <Box>
                    <Typography
                        sx={{
                            color: '#1E293B',
                            fontWeight: 600,
                            fontSize: { xs: '1.5rem', md: '1.75rem' },
                            lineHeight: 1.2
                        }}
                    >
                        Hosts & Partners
                    </Typography>
                    <Typography sx={{ color: '#64748B', fontSize: '0.82rem', mt: 0.5 }}>
                        {loading ? 'Loading hosts...' : `${totalCount} hosts · ${activeCount} active · One account per partner — centres and gurus live in Directory`}
                    </Typography>
                </Box>

                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => fetchHosts(page, debouncedSearch, statusFilter, kycFilter)}
                    startIcon={<RefreshCw size={14} />}
                    disabled={loading}
                    sx={{
                        color: '#64748B',
                        textTransform: 'none',
                        fontSize: '0.8rem',
                        fontWeight: 500,
                        height: '36px',
                        px: 1.5,
                        borderRadius: '6px',
                        border: '1px solid #E2E8F0',
                        bgcolor: '#FFFFFF',
                        boxShadow: 'none',
                        '&:hover': {
                            bgcolor: '#F8FAFC',
                            borderColor: '#CBD5E1',
                            color: '#1E293B',
                            boxShadow: 'none'
                        }
                    }}
                >
                    Refresh
                </Button>
            </Stack>

            {/* Error Notification */}
            {error && (
                <Alert severity="error" sx={{ mb: 2.5, borderRadius: '6px', fontSize: '0.82rem', boxShadow: 'none' }}>
                    {error}
                </Alert>
            )}

            {/* ── Search & Filter Controls Card ── */}
            <Paper
                elevation={0}
                sx={{
                    p: 1.5,
                    mb: 2.5,
                    borderRadius: '8px',
                    border: '1px solid #E8E8E8',
                    bgcolor: '#FFFFFF',
                    boxShadow: 'none'
                }}
            >
                <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap" useFlexGap sx={{ gap: 1.25 }}>
                    {/* Search host name or email */}
                    <TextField
                        placeholder="Search by host name, email, location..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        variant="outlined"
                        size="small"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start" sx={{ mr: 0.75 }}>
                                    <Search size={16} color="#94A3B8" />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            flex: { xs: '1 1 100%', sm: '1 1 200px', md: '0 1 280px' },
                            bgcolor: '#FFFFFF',
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '6px',
                                height: '38px',
                                fontSize: '0.82rem',
                                '& fieldset': { borderColor: '#E2E8F0' },
                                '&:hover fieldset': { borderColor: '#CBD5E1' },
                                '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' },
                            },
                        }}
                    />

                    {/* Status Filter */}
                    <FormControl size="small" sx={{ minWidth: { xs: 'calc(50% - 6px)', sm: 130 } }}>
                        <Select
                            displayEmpty
                            value={statusFilter}
                            onChange={(e) => {
                                setStatusFilter(e.target.value);
                                setPage(1);
                            }}
                            MenuProps={selectMenuProps}
                            renderValue={(selected) =>
                                selected ? (STATUS_LABELS[selected] || selected) : <span style={{ color: '#64748B' }}>Status</span>
                            }
                            sx={{
                                borderRadius: '6px',
                                height: '38px',
                                fontSize: '0.82rem',
                                fontWeight: 500,
                                color: '#1E293B',
                                bgcolor: '#FFFFFF',
                                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E2E8F0' },
                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#CBD5E1' },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#FFBD65', borderWidth: '1px' },
                            }}
                        >
                            <MenuItem value="">All Statuses</MenuItem>
                            <MenuItem value="active">Active</MenuItem>
                            <MenuItem value="inactive">Inactive</MenuItem>
                            <MenuItem value="pending">Pending</MenuItem>
                            <MenuItem value="suspended">Suspended</MenuItem>
                        </Select>
                    </FormControl>

                    {/* KYC Filter */}
                    <FormControl size="small" sx={{ minWidth: { xs: 'calc(50% - 6px)', sm: 120 } }}>
                        <Select
                            displayEmpty
                            value={kycFilter}
                            onChange={(e) => {
                                setKycFilter(e.target.value);
                                setPage(1);
                            }}
                            MenuProps={selectMenuProps}
                            renderValue={(selected) =>
                                selected ? (KYC_LABELS[selected] || selected) : <span style={{ color: '#64748B' }}>KYC</span>
                            }
                            sx={{
                                borderRadius: '6px',
                                height: '38px',
                                fontSize: '0.82rem',
                                fontWeight: 500,
                                color: '#1E293B',
                                bgcolor: '#FFFFFF',
                                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E2E8F0' },
                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#CBD5E1' },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#FFBD65', borderWidth: '1px' },
                            }}
                        >
                            <MenuItem value="">All KYC</MenuItem>
                            <MenuItem value="verified">Verified</MenuItem>
                            <MenuItem value="pending">Pending</MenuItem>
                            <MenuItem value="rejected">Rejected</MenuItem>
                        </Select>
                    </FormControl>

                    {/* Reset Filters Button */}
                    {(searchTerm || statusFilter || kycFilter) && (
                        <Button
                            variant="text"
                            size="small"
                            onClick={handleClearFilters}
                            startIcon={<RotateCcw size={14} />}
                            disableElevation
                            disableFocusRipple
                            disableRipple
                            disableTouchRipple
                            sx={{
                                color: '#64748B',
                                textTransform: 'none',
                                fontSize: '0.8rem',
                                fontWeight: 500,
                                height: '38px',
                                px: 1.5,
                                borderRadius: '6px',
                                border: '1px solid #E2E8F0',
                                '&:hover': {
                                    bgcolor: '#F8FAFC',
                                    borderColor: '#CBD5E1',
                                    color: '#1E293B'
                                }
                            }}
                        >
                            Reset
                        </Button>
                    )}
                </Stack>
            </Paper>

            {/* ── Main Hosts Table ── */}
            <Paper
                elevation={0}
                sx={{
                    borderRadius: "8px",
                    border: "1px solid #E8E8E8",
                    bgcolor: '#FFFFFF',
                    overflow: "hidden",
                    boxShadow: 'none'
                }}
            >
                <TableContainer>
                    <Table sx={{ minWidth: 950 }}>
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#F8FAFC', borderBottom: '2px solid #EEF2F6' }}>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 2 }}>HOST</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5 }}>LOCATION</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.25, textAlign: 'center' }}>LISTINGS</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.25, textAlign: 'center' }}>BOOKINGS</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5 }}>GMV</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5 }}>SLAB</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.25, textAlign: 'center' }}>RATING</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, textAlign: 'center' }}>KYC</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, textAlign: 'center' }}>STATUS</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 2, textAlign: 'center' }}>ACTIONS</TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={10} align="center" sx={{ py: 8 }}>
                                        <CircularProgress size={24} sx={{ color: '#FFBD65' }} />
                                    </TableCell>
                                </TableRow>
                            ) : processedHosts.length > 0 ? (
                                processedHosts.map((host) => {
                                    const kycStyle = KYC_STYLES[host.kyc] || DEFAULT_BADGE_STYLE;
                                    const statusStyle = STATUS_STYLES[host.status] || DEFAULT_BADGE_STYLE;

                                    return (
                                        <TableRow
                                            key={host.id || host.email}
                                            sx={{
                                                '& td': { borderBottom: '1px solid #F0F0F0' },
                                                '&:last-child td': { borderBottom: 0 },
                                            }}
                                        >
                                            {/* HOST (Name + Email) */}
                                            <TableCell sx={{ py: 1.25, px: 2 }}>
                                                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#1E293B', lineHeight: 1.3 }}>
                                                    {host.name}
                                                </Typography>
                                                <Typography sx={{ fontSize: '0.75rem', color: '#64748B', mt: 0.2 }}>
                                                    {host.email}
                                                </Typography>
                                            </TableCell>

                                            {/* LOCATION */}
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.78rem', color: '#334155' }}>
                                                {host.location}
                                            </TableCell>

                                            {/* LISTINGS */}
                                            <TableCell sx={{ py: 1.25, px: 1.25, textAlign: 'center', fontSize: '0.78rem', color: '#334155', fontWeight: 500 }}>
                                                {host.listings}
                                            </TableCell>

                                            {/* BOOKINGS */}
                                            <TableCell sx={{ py: 1.25, px: 1.25, textAlign: 'center', fontSize: '0.78rem', color: '#334155', fontWeight: 500 }}>
                                                {host.bookings}
                                            </TableCell>

                                            {/* GMV */}
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.78rem', fontWeight: 500, color: '#1E293B' }}>
                                                {host.gmv}
                                            </TableCell>

                                            {/* SLAB */}
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.78rem', color: '#334155' }}>
                                                {host.slab}
                                            </TableCell>

                                            {/* RATING */}
                                            <TableCell sx={{ py: 1.25, px: 1.25, textAlign: 'center', fontSize: '0.78rem', color: '#334155' }}>
                                                {host.rating ? host.rating : '—'}
                                            </TableCell>

                                            {/* KYC BADGE */}
                                            <TableCell sx={{ py: 1.25, px: 1.5, textAlign: 'center' }}>
                                                <Box
                                                    sx={{
                                                        px: 1.2,
                                                        py: 0.35,
                                                        borderRadius: '4px',
                                                        fontSize: '0.7rem',
                                                        fontWeight: 500,
                                                        display: 'inline-block',
                                                        bgcolor: kycStyle.bg,
                                                        color: kycStyle.color,
                                                        border: `1px solid ${kycStyle.border}`,
                                                        textTransform: 'lowercase'
                                                    }}
                                                >
                                                    {host.kyc}
                                                </Box>
                                            </TableCell>

                                            {/* STATUS BADGE */}
                                            <TableCell sx={{ py: 1.25, px: 1.5, textAlign: 'center' }}>
                                                <Box
                                                    sx={{
                                                        px: 1.2,
                                                        py: 0.35,
                                                        borderRadius: '4px',
                                                        fontSize: '0.7rem',
                                                        fontWeight: 500,
                                                        display: 'inline-block',
                                                        bgcolor: statusStyle.bg,
                                                        color: statusStyle.color,
                                                        border: `1px solid ${statusStyle.border}`,
                                                        textTransform: 'lowercase'
                                                    }}
                                                >
                                                    {host.status}
                                                </Box>
                                            </TableCell>

                                            {/* ACTIONS (Verify and View side by side) */}
                                            <TableCell sx={{ py: 1.25, px: 2, textAlign: 'center' }}>
                                                <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        onClick={() => handleOpenKycView(host)}
                                                        disableElevation
                                                        disableFocusRipple
                                                        disableRipple
                                                        disableTouchRipple
                                                        sx={{
                                                            textTransform: 'none',
                                                            fontSize: '0.75rem',
                                                            fontWeight: 500,
                                                            color: '#1E293B',
                                                            borderColor: '#CBD5E1',
                                                            borderRadius: '4px',
                                                            height: '28px',
                                                            py: 0,
                                                            px: 1.5,
                                                            minWidth: '54px',
                                                            bgcolor: '#FFFFFF',
                                                            boxShadow: 'none',
                                                            '&:hover': {
                                                                bgcolor: '#F8FAFC',
                                                                borderColor: '#94A3B8',
                                                                boxShadow: 'none'
                                                            }
                                                        }}
                                                    >
                                                        Verify
                                                    </Button>

                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        onClick={() => handleOpenProfile(host)}
                                                        disableElevation
                                                        disableFocusRipple
                                                        disableRipple
                                                        disableTouchRipple
                                                        sx={{
                                                            textTransform: 'none',
                                                            fontSize: '0.75rem',
                                                            fontWeight: 500,
                                                            color: '#1E293B',
                                                            borderColor: '#CBD5E1',
                                                            borderRadius: '4px',
                                                            height: '28px',
                                                            py: 0,
                                                            px: 1.5,
                                                            minWidth: '54px',
                                                            bgcolor: '#FFFFFF',
                                                            boxShadow: 'none',
                                                            '&:hover': {
                                                                bgcolor: '#F8FAFC',
                                                                borderColor: '#94A3B8',
                                                                boxShadow: 'none'
                                                            }
                                                        }}
                                                    >
                                                        View
                                                    </Button>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={10} align="center" sx={{ py: 6, color: '#64748B', fontSize: '0.8rem' }}>
                                        No hosts or partners match your selected criteria.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Pagination Controls */}
            <GlobalPagination
                page={page}
                pageSize={limit}
                totalRecords={pagination.totalItems || processedHosts.length}
                totalPages={pagination.totalPages}
                onPageChange={(newPage) => {
                    setPage(newPage);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                onPageSizeChange={(newLimit) => {
                    setLimit(newLimit);
                    setPage(1);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                loading={loading}
            />
        </Box>
    );
};

export default Hosts;
